import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSettings, type InitialSettingsData } from '../services/settingServices';

interface SettingsContextType {
  settings: InitialSettingsData | null;
  isLoading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<InitialSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getSettings();
      setSettings(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load app settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch API ONCE when the app mounts
  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, isLoading, error, refreshSettings: fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom Hook to easily use settings in any form component
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};